import clsx from 'clsx'
import { brand } from '@/brand/active.ts'
import { EvtWordmark, GiMark } from '@/brand/marks.tsx'
import { Orb } from '@/components/ui/Orb.tsx'
import { useT } from '@/i18n/index.ts'

/**
 * The two places a brand shows itself, as components the rest of the app can call without
 * knowing which brand is running.
 *
 * `BrandLockup` is the organisation's mark — the top of the rail, the foot of the sign-in
 * panel. `AssistantMark` is the model's own face — the home hero, the byline over an answer,
 * the sign-in hero. See `profiles.ts` for why those are two slots and not one.
 *
 * Both fall back to what the product ships with, so a deployment that configures nothing
 * renders exactly the design ADR-0012 describes rather than a gap where a logo would be.
 */

/**
 * The organisation's lockup.
 *
 * Sized by height, because that is the dimension a row of chrome actually constrains: a
 * logotype and a symbol have wildly different aspect ratios, and asking call sites for a
 * width would make the EVT mark and the Gi mark disagree about how tall the rail header is.
 *
 * A logotype stands alone; a symbol gets the name in type beside it. Spelling "EVT" next to
 * the EVT logo is the single most common way a branded deployment looks amateur, and a bare
 * sprout with nothing beside it is the other one.
 *
 * The mark inside is never named to a screen reader. Every placement is inside a link that
 * carries the whole lockup's accessible name already, so a name here would be read twice.
 */
export const BrandLockup = ({ height = 25, className }: { height?: number; className?: string }) => {
  // In the body, never at module scope: the fallback below is prose, and a capture taken at
  // import would keep saying it in the previous language after a switch (PRD-27 F-3).
  const t = useT()

  if (brand.wordmark === 'evt-wordmark') {
    // 0.78, because a logotype's cap height has to sit against the x-height of the type in
    // the other two lockups rather than against their overall box, or it reads as oversized.
    return <EvtWordmark height={height * 0.78} className={className} />
  }

  if (brand.wordmark === 'gi-sprout') {
    return (
      <span className={clsx('flex min-w-0 items-center gap-2', className)}>
        <GiMark size={height} />
        {/* A profile with no organisation of its own falls back to the catalogue's prose
            placeholder, which is a sentence fragment and not a name (PRD-27 D-13). */}
        <span className="truncate text-[16px] font-semibold tracking-[-0.03em]">
          {brand.organisation ?? t.brand.organisation}
        </span>
      </span>
    )
  }

  return (
    <span className={clsx('flex min-w-0 items-center gap-2.5', className)}>
      <Orb size={height} className="shadow-[var(--irid-glow)]" />
      <span className="truncate text-[16px] font-semibold tracking-[-0.03em]">{brand.assistant}</span>
    </span>
  )
}

/**
 * The assistant's face.
 *
 * Falls through to the orb, which is the right answer for most deployments: the orb is
 * painted from the brand seeds, so it arrives in the customer's colour without anyone
 * drawing anything. A profile sets `avatar` only when its assistant genuinely has a mark of
 * its own — see the note in `Orb.tsx` about what that mark is allowed to mean.
 */
export const AssistantMark = ({
  size = 22,
  hero = false,
  className
}: {
  size?: number
  hero?: boolean
  className?: string
}) => {
  // Unnamed, like the orb it stands in for: every placement sits beside text that already
  // says whose presence this is — the byline names the agent, the hero carries the greeting.
  if (brand.avatar === 'gi-sprout') {
    return <GiMark size={size} className={className} />
  }
  return <Orb size={size} hero={hero} className={className} />
}
